import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Snackbar, Stack, Tooltip, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import dayjs from "dayjs";
import PageMeta from "../components/common/PageMeta";
import PageHeader from "../components/common/PageHeader";
import DisputeStatusChip from "../components/disputes/DisputeStatusChip";
import PpLogo from "../components/common/PpLogo";
import { textLinkSx } from "../components/common/textLinkStyles";
import { SectionHeading } from "../components/evidence/ChecklistRow";
import ConclusionRow from "../components/evidence/ConclusionRow";
import EvidenceActions, { CaseDetailsCard } from "../components/evidence/EvidenceActions";
import CaseLookupRows from "../components/evidence/CaseLookupRows";
import CaseStorefrontRow from "../components/evidence/CaseStorefrontRow";
import EvidenceHelpDialog, { HelpMark, type HelpTab } from "../components/evidence/EvidenceHelpDialog";
import EvidencePreviewPane, { type PreviewControls } from "../components/evidence/EvidencePreviewPane";
import { sectionOfId } from "../components/evidence/packageFiles";
import GoodToKnow from "../components/evidence/GoodToKnow";
import OrderTypePrompt from "../components/evidence/OrderTypePrompt";
import ProductDescriptionCard from "../components/evidence/ProductDescriptionCard";
import SectionsCard, { type SectionRowKey } from "../components/evidence/SectionsCard";
import SubmissionColumn from "../components/evidence/SubmissionColumn";
import { useEvidenceAttachments } from "../hooks/useEvidenceAttachments";
import { useEvidenceDraft } from "../hooks/useEvidenceDraft";
import { formatCurrency } from "../utils/formatCurrency";
import { getTimeRemaining } from "../utils/timeRemaining";
import { ucFirstWord } from "../utils/textUtils";
import { respondByLeft } from "../utils/bankCaseUtils";
import EditBankCaseDialog from "../components/evidenceBuilder/EditBankCaseDialog";
import ChangeOrderDialog from "../components/evidenceBuilder/ChangeOrderDialog";
import FindOrderDialog from "../components/evidence/FindOrderDialog";
import { EVIDENCE_PROVIDERS, PACKAGE_PROVIDERS, type DocumentProvider, type PackageProvider, type SubmissionProvider } from "../types/evidence";
import { useStateValue } from "../context/StateValueContext";
import { hasPermission } from "../functions/permissionUtils";
import { PERMISSIONS } from "../constants/permissions";

// AppShell pads page content with p-4 / md:p-6, so that padding is the gap left below the row.
const CONTENT_GAP_PX = 16;
const CONTENT_GAP_MD_PX = 24;
const MD_BREAKPOINT_PX = 768;
// Below this the 380px sidebar and a document preview cannot share a row, so they stack.
const STACK_BREAKPOINT_PX = 900;
// The submission column is as wide as the left one; below this the preview between them gets too narrow, so it moves under.
const SUBMISSION_BREAKPOINT_PX = 1590;
// A short viewport still gets a usable workspace; the left column scrolls inside it.
const MIN_WORKSPACE_PX = 320;

const CASE_FLASH_MS = 1200;
const ANSWER_KEYS: Record<Exclude<SubmissionProvider, "adyen">, "shopifyAnswers" | "paypalNotes" | "stripeAnswers"> = { shopify: "shopifyAnswers", paypal: "paypalNotes", stripe: "stripeAnswers" };

export default function DisputeEvidence() {
  const { disputeId, bankCaseId } = useParams();
  const {
    dispute, bankCase, draft, outline, outlineError, previewHtml, previewStale, previewExpired, previewError, loadError, draftLoadFailed, draftLoaded, orderTypeRequired, orderPickRequired,
    inProgress, waitOverdue, isStale, staleChanges, trackingPending, pendingAction, canDownload,
    submission, submissionError, submitError,
    createDraft, generate, stopWaiting, saveManualFields, submitEvidence, saveConclusion, rewriteConclusion, saveBankFacts, changeOrder, chooseOrder,
    onAttachmentsChange, downloadPdf, downloadPackageFile, retryLoad, retryPreview,
    submissionProvider,
    toast, setToast,
  } = useEvidenceDraft(bankCaseId ? { kind: "bank_case", id: bankCaseId } : { kind: "dispute", id: disputeId });

  // The builder opened this case instead of creating one. Said once: the state is dropped so a
  // reload does not say it again.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!(location.state as { existingCase?: boolean } | null)?.existingCase) return;
    setToast({ message: "This dispute ID already has a case. Nothing new was created.", severity: "info" });
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate, setToast]);

  const attachments = useEvidenceAttachments(draft?.draft_id, inProgress, onAttachmentsChange);
  const [arnUnsaved, setArnUnsaved] = useState(false);
  const [contactUnsaved, setContactUnsaved] = useState(false);
  const [refundUnsaved, setRefundUnsaved] = useState(false);
  const [digitalDetailsUnsaved, setDigitalDetailsUnsaved] = useState(false);
  const [help, setHelp] = useState<HelpTab | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);
  const [changingOrder, setChangingOrder] = useState(false);
  const [findingOrder, setFindingOrder] = useState(false);
  const [{ user }] = useStateValue();
  // The storefront settings live on the company page, which only this permission opens.
  const canOpenStorefrontSetup = hasPermission(user, PERMISSIONS.COMPANIES_WRITE_ALL);
  const provider: DocumentProvider | null = bankCase
    ? "bank"
    : dispute && (EVIDENCE_PROVIDERS as readonly string[]).includes(dispute.pp_type) ? dispute.pp_type as DocumentProvider : null;
  const packageProvider = provider && (PACKAGE_PROVIDERS as readonly string[]).includes(provider) ? provider as PackageProvider : null;
  // Whichever the page was opened on; the cards below need one of them loaded, not a particular one.
  const loaded = Boolean(dispute ?? bankCase);
  // Stripe's key is probed by the view itself, so the view's verdict outranks the list's; the others carry it on the list row.
  const canSubmitFlag = submission?.provider === "stripe" ? submission.canSubmit : dispute?.can_submit_evidence ?? null;
  // Its own permission: the column stays off every agent's page until they are given it.
  const showSubmission = submissionProvider !== null && canSubmitFlag !== null && Boolean(draft);

  const workspaceRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<PreviewControls | null>(null);
  const [focusSection, setFocusSection] = useState<{ key: SectionRowKey; at: number } | null>(null);
  const [hoverSection, setHoverSection] = useState<SectionRowKey | null>(null);
  const caseRef = useRef<HTMLDivElement>(null);
  const [caseFocus, setCaseFocus] = useState<number | null>(null);
  const [caseFlashed, setCaseFlashed] = useState(false);
  const [caseHover, setCaseHover] = useState(false);
  // A reloaded preview has nothing under the pointer until it moves again.
  useEffect(() => {
    setHoverSection(null);
    setCaseHover(false);
  }, [previewHtml]);
  useEffect(() => {
    if (caseFocus === null) return;
    setCaseFlashed(true);
    caseRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    const timer = setTimeout(() => setCaseFlashed(false), CASE_FLASH_MS);
    return () => { clearTimeout(timer); setCaseFlashed(false); };
  }, [caseFocus]);
  const renderedIds = previewHtml ? [...previewHtml.matchAll(/data-section="([^"]+)"/g)].map((match) => match[1]) : [];
  const [workspaceHeight, setWorkspaceHeight] = useState(MIN_WORKSPACE_PX);
  const [stacked, setStacked] = useState(false);
  const [submissionStacked, setSubmissionStacked] = useState(false);
  // A wrapped submission column needs the row to grow like a stacked one.
  const wraps = stacked || (showSubmission && submissionStacked);

  // The row's top edge moves with the app header, the billing banner and the page header, so the
  // height left in the viewport is measured instead of hard-coded.
  useEffect(() => {
    // The row being sized sits inside the observed body, so the write is deferred to the next
    // frame and skipped when the height is unchanged; measuring inline re-fires the observer.
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const top = workspaceRef.current?.getBoundingClientRect().top;
        if (top === undefined) return;
        const gap = window.innerWidth >= MD_BREAKPOINT_PX ? CONTENT_GAP_MD_PX : CONTENT_GAP_PX;
        const next = Math.max(window.innerHeight - top - gap, MIN_WORKSPACE_PX);
        setWorkspaceHeight((prev) => (prev === next ? prev : next));
        setStacked(window.innerWidth < STACK_BREAKPOINT_PX);
        setSubmissionStacked(window.innerWidth < SUBMISSION_BREAKPOINT_PX);
      });
    };
    measure();
    window.addEventListener("resize", measure);
    // Everything above the row — the billing banner's own fetch, the page header, the status chip —
    // mounts on its own schedule, so any layout change re-measures rather than a dependency list.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, []);

  return (
    <div>
      <PageMeta title="Chargemont | Dispute Evidence" description="Generate and preview dispute evidence" />
      <PageHeader
        title={bankCaseId
          ? `Evidence — ${bankCase?.bank_name ?? "Bank"} dispute ${bankCase?.dispute_reference ?? ""}`.trimEnd()
          : `Evidence — Dispute #${disputeId}`}
        action={dispute ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <DisputeStatusChip status={dispute.status} />
            <PpLogo ppType={dispute.pp_type} />
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600 }}>
                {formatCurrency(dispute.pp_amount, dispute.pp_currency)} {dispute.pp_currency.toUpperCase()}
              </Box>
              {` · ${ucFirstWord(dispute.pp_reason)} · Evidence due `}
              {dispute.pp_evidence_due_by
                ? `${dayjs.utc(dispute.pp_evidence_due_by).format("MMM DD HH:mm [UTC]")} (${getTimeRemaining(dispute.pp_evidence_due_by, dayjs.utc())})`
                : "--"}
            </Typography>
            <Tooltip title="Help: how evidence works">
              <IconButton aria-label="Help: how evidence works" onClick={() => setHelp("how")} size="small"><HelpOutlineIcon /></IconButton>
            </Tooltip>
          </Box>
        ) : bankCase ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600 }}>
                {formatCurrency(bankCase.amount, bankCase.currency)} {bankCase.currency}
              </Box>
              {` · ${bankCase.reason_text || ucFirstWord(bankCase.reason_code)} · Respond by `}
              {bankCase.respond_by
                // A day in the bank's portal, not an instant: it runs to the end of that day.
                ? `${dayjs.utc(bankCase.respond_by).format("MMM D, YYYY")} (${respondByLeft(bankCase.respond_by).left})`
                : "--"}
            </Typography>
            <Tooltip title="Help: how evidence works">
              <IconButton aria-label="Help: how evidence works" onClick={() => setHelp("how")} size="small"><HelpOutlineIcon /></IconButton>
            </Tooltip>
          </Box>
        ) : null}
      />

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={retryLoad}>Retry</Button>}
        >
          {loadError}
        </Alert>
      )}

      <Box
        ref={workspaceRef}
        sx={{
          display: "flex",
          flexDirection: stacked ? "column" : "row",
          flexWrap: showSubmission && !stacked && submissionStacked ? "wrap" : "nowrap",
          gap: 2,
          alignItems: "stretch",
          // Stacked, the panes grow with their content rather than splitting one viewport height.
          ...(wraps ? { minHeight: workspaceHeight } : { height: workspaceHeight }),
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: stacked ? "100%" : 380,
            flexShrink: 0,
            minHeight: 0,
            overflowY: wraps ? "visible" : "auto",
            pr: 0.5,
          }}
        >
          {!draft && !draftLoadFailed && loaded && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionHeading action={<HelpMark label="How evidence generation works" onClick={() => setHelp("how")} />}>Case</SectionHeading>
              <Typography variant="body2" color="text.secondary">
                Builds the document from the order. You review every section after.
              </Typography>
              {dispute?.paypal_buyer_email && (
                <Box sx={{ mt: 1.5 }}>
                  <CaseLookupRows orderName={null} orderSource={null} customerEmail={null} paypalBuyerEmail={dispute.paypal_buyer_email} />
                </Box>
              )}
              {/* Never offered while the draft GET failed: `draft` is null then too, and generating
                  would supersede the draft the server actually holds. */}
              <Box sx={{ mt: 2 }}>
                {orderTypeRequired ? (
                  <OrderTypePrompt
                    disabled={pendingAction === "generate" || !draftLoaded}
                    onPick={(orderType) => { void createDraft(orderType); }}
                  />
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => { void createDraft(); }}
                    disabled={pendingAction === "generate" || !draftLoaded}
                    startIcon={pendingAction === "generate" ? <CircularProgress size={16} /> : null}
                  >
                    Generate evidence
                  </Button>
                )}
                {orderPickRequired && disputeId && (
                  <Button sx={{ ml: 1.5 }} onClick={() => setFindingOrder(true)} disabled={pendingAction === "generate"}>
                    Find the order
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          {draft && loaded && (
            <>
              <EvidenceActions
                draft={draft}
                isStale={isStale}
                staleChanges={staleChanges}
                previewStale={previewStale}
                trackingPending={trackingPending}
                inProgress={inProgress}
                waitOverdue={waitOverdue}
                pending={pendingAction !== null}
                canDownload={canDownload}
                fieldsUnsaved={arnUnsaved || contactUnsaved || refundUnsaved || digitalDetailsUnsaved}
                onGenerate={generate}
                onDownload={downloadPdf}
                onStopWaiting={stopWaiting}
                packageProvider={packageProvider}
                onDownloadPackageFile={downloadPackageFile}
              />
              <Paper
                ref={caseRef}
                variant="outlined"
                sx={{ p: "14px 16px", display: "flex", flexDirection: "column", gap: "10px", bgcolor: caseFlashed ? "action.selected" : caseHover ? "action.hover" : undefined }}
              >
                <SectionHeading
                  action={(
                    <>
                      <HelpMark label="How evidence generation works" onClick={() => setHelp("how")} />
                      {bankCase && (
                        <>
                          <Button size="small" sx={{ ...textLinkSx, ml: "auto" }} disabled={inProgress || pendingAction === "generate"} onClick={() => setChangingOrder(true)}>
                            Change order
                          </Button>
                          <Button size="small" sx={{ ...textLinkSx, ml: 1.5 }} disabled={inProgress || pendingAction === "generate"} onClick={() => setEditingDetails(true)}>
                            Edit details
                          </Button>
                        </>
                      )}
                    </>
                  )}
                >
                  Case
                </SectionHeading>
                <CaseLookupRows
                  bank={bankCase && [bankCase.bank_name, bankCase.dispute_reference].filter(Boolean).join(" · ")}
                  orderName={draft.generated_order_name ?? bankCase?.order_name ?? null}
                  orderSource={draft.generated_order_source ?? bankCase?.order_source ?? null}
                  customerEmail={draft.generated_customer_email}
                  paypalBuyerEmail={dispute?.paypal_buyer_email ?? null}
                  generated={draft.generated_at !== null}
                  storefront={outline?.storefront && (
                    <CaseStorefrontRow
                      storefront={outline.storefront}
                      unresolved={Boolean(draft.attention_items?.some((item) => item.code === "storefront_unresolved"))}
                      saved={draft.input_overrides?.storefrontId ?? null}
                      disabled={inProgress || pendingAction === "generate"}
                      settingsLink={canOpenStorefrontSetup}
                      onSave={(storefrontId) => saveManualFields({ storefrontId }, "Failed to save the storefront")}
                    />
                  )}
                />
                <CaseDetailsCard
                  draft={draft}
                  inProgress={inProgress}
                  showArn={provider !== null && provider !== "paypal"}
                  onSaveArn={(value) => saveManualFields({ arn: value }, "Failed to save the ARN")}
                  onUnsavedChange={setArnUnsaved}
                />
              </Paper>
              <GoodToKnow
                items={draft.attention_items ?? []}
                rowKeys={outline?.sections.map((s) => s.key) ?? []}
                settingsLink={canOpenStorefrontSetup}
                help={<HelpMark label="Why sections are left out" onClick={() => setHelp("setup")} />}
              />
              <SectionsCard
                draft={draft}
                outline={outline}
                items={draft.attention_items ?? []}
                attachments={attachments}
                disabled={inProgress || pendingAction === "generate"}
                error={outlineError}
                provider={packageProvider}
                renderedIds={renderedIds}
                onJump={(ids) => previewRef.current?.jumpTo(ids)}
                onHover={(ids) => previewRef.current?.highlight(ids)}
                focusSection={focusSection}
                hoverSection={hoverSection}
                productDescription={(
                  <ProductDescriptionCard
                    draft={draft}
                    attachments={attachments}
                    disabled={inProgress}
                    onSave={(product, text) => saveManualFields({ productDescriptions: { [product]: text } }, "Failed to save the product description")}
                    embedded
                  />
                )}
                conclusion={(
                  <ConclusionRow
                    draft={draft}
                    disabled={inProgress}
                    rewriting={pendingAction === "conclusion"}
                    onSave={saveConclusion}
                    onRewrite={rewriteConclusion}
                    embedded
                  />
                )}
                onSaveExcludes={(body) => saveManualFields(body, "Failed to save the section choice")}
                onSaveRefund={(value) => saveManualFields({ manualRefund: value }, "Failed to save the refund details")}
                onSaveContact={(fields) => saveManualFields(fields, "Failed to save the customer contact")}
                onSaveDigitalDetails={(value) => saveManualFields({ digitalDetails: value }, "Failed to save the portal details")}
                onSaveTrackingNumber={(value) => saveManualFields({ trackingNumber: value }, "Failed to save the tracking number")}
                onContactUnsavedChange={setContactUnsaved}
                onRefundUnsavedChange={setRefundUnsaved}
                onDigitalDetailsUnsavedChange={setDigitalDetailsUnsaved}
              />
            </>
          )}
        </Stack>

        <EvidencePreviewPane
          html={previewHtml}
          hasDraft={!!draft}
          previewStale={previewStale}
          previewExpired={previewExpired}
          previewError={previewError}
          onRetry={retryPreview}
          onDownloadPdf={downloadPdf}
          inProgress={inProgress}
          status={draft?.status}
          stacked={wraps}
          controls={previewRef}
          provider={packageProvider}
          onSectionHover={(sectionId) => {
            const target = sectionId ? sectionOfId(sectionId) : null;
            setCaseHover(target === "case");
            setHoverSection(target && target !== "case" ? target : null);
          }}
          onSectionClick={(sectionId) => {
            const target = sectionOfId(sectionId);
            if (target === "case") setCaseFocus(Date.now());
            else if (target) setFocusSection({ key: target, at: Date.now() });
          }}
        />

        {showSubmission && draft && submissionProvider && (
          <Box sx={{ width: submissionStacked || stacked ? "100%" : 380, flexShrink: 0, minHeight: 0, display: "flex", flexDirection: "column", pl: submissionStacked || stacked ? 0 : 0.5 }}>
            <SubmissionColumn
              provider={submissionProvider}
              draft={draft}
              canSubmit={canSubmitFlag === true}
              evidenceDueBy={dispute?.pp_evidence_due_by ?? null}
              ppStatus={dispute?.pp_status ?? null}
              submission={submission}
              submissionError={submissionError}
              submitError={submitError}
              documentStale={isStale}
              inProgress={inProgress}
              submitting={pendingAction === "submit"}
              settingsLink={canOpenStorefrontSetup}
              storefront={outline?.storefront ?? null}
              onHelp={() => setHelp("how")}
              onSaveAnswer={(field, text) => saveManualFields(
                submissionProvider === "adyen" ? { adyenDefense: text === null ? null : { reasonCode: text } } : { [ANSWER_KEYS[submissionProvider]]: { [field]: text } },
                "Failed to save the answer",
              )}
              onSubmit={submitEvidence}
            />
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
      <EvidenceHelpDialog tab={help} provider={provider} onTabChange={setHelp} onClose={() => setHelp(null)} />
      {bankCase && editingDetails && <EditBankCaseDialog bankCase={bankCase} onClose={() => setEditingDetails(false)} onSave={saveBankFacts} />}
      {bankCase && changingOrder && <ChangeOrderDialog bankCase={bankCase} onClose={() => setChangingOrder(false)} onChange={changeOrder} />}
      {disputeId && findingOrder && <FindOrderDialog key={disputeId} disputeId={disputeId} onClose={() => setFindingOrder(false)} onPick={chooseOrder} />}
    </div>
  );
}
